/*
 * Ticket-transfer client (PROJ-0078-WU-10; design note
 * docs/current/DESIGN-TRANSFER-RECIPIENT-STEP-V1.md, ratified by boardroom
 * dispatch BOARDROOM-2026-09-27-191409).
 *
 * CONTRACT: code_store contracts / TICKETS-CONTRACT-TRANSFER-HOLD-V1 (tickets,
 * v3 PUBLISHED, source tickets main with #264/#266/#267/#273/#275/#276/#278/
 * #280 merged). Build is to BACKEND-V1 / the tickets code: no downgrade-
 * surplus fee offset and no heat hold — the UI renders the returned `preview`
 * verbatim and never computes a price or a fee. Since v3 (#276)
 * order.total_minor INCLUDES the transfer fee, so it is the ONE number that
 * decides free vs paid (see isFreeHold).
 *
 * Mirrors fetchConsumerOrders (./api.ts): base = ticketsApiBase()
 * (VITE_TICKETS_API_BASE, already ending in /box-office), Bearer <consumer ID
 * token>, and a Bearer-path 401 surfaces as an ApiAuthError subclass so the
 * caller re-authenticates instead of showing a generic failure.
 */
import { ApiAuthError } from './api'

/** Guest-link proof header (TICKETS-CONTRACT-TRANSFER-HOLD-V1 <auth>
 *  guest_link): value is the compact `<ref>.<exp>.<sig>` token, forwarded
 *  UNCHANGED from /transfer/:token. Server precedence: Authorization Bearer,
 *  then this header, then body.transfer_link. LIVE since tickets #273 (v3). */
export const TRANSFER_LINK_HEADER = 'X-Transfer-Link'

/** How the hold request authenticates: the signed-in buyer's Cognito ID token,
 *  or (guest landing, decision (c)) the signed transfer-link token, sent in
 *  TRANSFER_LINK_HEADER *instead of* the Bearer. my.trumin never mints,
 *  verifies or decodes the link token — it only forwards it. */
export type TransferAuth =
  | { kind: 'bearer'; idToken: string }
  | { kind: 'link'; token: string }

export interface TransferHoldInput {
  /** Box-office tickets.ticket_id = orders[].tickets[].id (contract <request>). */
  sourceTicketId: string
  /** REQUIRED on every transfer (contract <request>). For a same-type PERSON
   *  transfer this is the source ticket's own ticket_type_id
   *  (orders[].tickets[].ticketTypeId — LIVE, tickets #275). */
  destTicketTypeId: string
  recipientEmail: string
}

/** Hold 201 body (contract <responses><hold>). Only the fields the UI reads
 *  are typed; the rest pass through. */
export interface TransferHoldResponse {
  transfer: {
    hold_expires_at: string
    status: string
    [k: string]: unknown
  }
  order: {
    order_id: string
    short_reference: string
    total_minor: number
    currency: string
    [k: string]: unknown
  }
  preview: {
    delta_minor: number
    fee_minor: number
    processing_fee_minor: number
    total_minor: number
    currency: string
    downgrade: boolean
    equal_price: boolean
    zero_total: boolean
    [k: string]: unknown
  }
}

export type TransferErrorKind =
  | 'unavailable' // 404 {"error":"not found"} (flag off) · 503 signing_not_configured · 503 transfer_line_kinds_unavailable
  | 'not_allowed' // 403 transfer_forbidden — not the owner / unknown ticket / other org
  | 'rule_denied' // 403 transfer_not_allowed_by_rule — no transfer rule allows it
  | 'not_active' // 409 source_ticket_not_active
  | 'hold_pending' // 409 transfer_hold_already_pending
  | 'auth' // 401 on the Bearer path (missing_proof / invalid_proof)
  | 'link_invalid' // 403 invalid_token (and any 401 on the link path)
  | 'link_expired' // 410 link_expired
  | 'lapsed' // client-side only: the hold countdown reached zero
  | 'invalid' // 400 (incl. v3 dest_ticket_type_id_invalid / person_transfer_type_mismatch)
  | 'unknown' // everything else, incl. v3 message-only 409s (capacity / org_unit charges)

/** Typed transfer failure. `code` is the server's machine code when it sent
 *  one (never the recipient email, never the token). */
export class TransferError extends Error {
  readonly kind: TransferErrorKind
  readonly status: number | null
  readonly code: string | null
  constructor(kind: TransferErrorKind, status: number | null = null, code: string | null = null) {
    super(`transfer failed: ${kind}${status !== null ? ` (${status})` : ''}`)
    this.kind = kind
    this.status = status
    this.code = code
  }
}

/** Bearer-path auth rejection — an ApiAuthError (so existing re-auth handling
 *  applies) that also carries `kind: 'auth'` like every other failure. */
export class TransferAuthError extends ApiAuthError {
  readonly kind = 'auth' as const
  readonly status: number
  readonly code: string | null
  constructor(status: number, code: string | null = null) {
    super(`transfer unauthorized (${status})`)
    this.status = status
    this.code = code
  }
}

export type TransferFailure = TransferError | TransferAuthError

/** Normalise anything thrown by createTransferHold to its kind. */
export function transferErrorKind(err: unknown): TransferErrorKind {
  if (err instanceof TransferAuthError || err instanceof ApiAuthError) return 'auth'
  if (err instanceof TransferError) return err.kind
  return 'unknown'
}

interface ErrorBody { error: string | null; code: string | null }

async function readErrorBody(res: Response): Promise<ErrorBody> {
  try {
    const body = (await res.json()) as { code?: unknown; error?: unknown } | null
    return {
      error: body && typeof body.error === 'string' ? body.error : null,
      code: body && typeof body.code === 'string' ? body.code : null,
    }
  } catch {
    // Non-JSON error body — the status alone decides the kind.
    return { error: null, code: null }
  }
}

/**
 * Map a non-2xx response to a typed failure — the TICKETS-CONTRACT-TRANSFER-
 * HOLD-V1 <errors> table. Unlisted codes (incl. an unlisted 403) fall to
 * 'unknown' (Contact support), NEVER to a sign-in prompt: only a 401 on the
 * Bearer path means the session is at fault.
 */
export function transferFailureFor(
  status: number,
  body: ErrorBody,
  authKind: TransferAuth['kind'] = 'bearer',
): TransferFailure {
  const { code } = body
  if (status === 404) {
    // Flag off is exactly {"error":"not found"}; other 404s (ticket_type /
    // event / org_unit not found) are message-only and are not "unavailable".
    return new TransferError(body.error === 'not found' ? 'unavailable' : 'unknown', status, code)
  }
  if (status === 401) {
    // Bearer path only per the row. On the link path the v3 codes are 403
    // invalid_token / 410 link_expired (#273); a 401 there is still a link
    // problem, never a sign-in prompt.
    return authKind === 'bearer' ? new TransferAuthError(status, code) : new TransferError('link_invalid', status, code)
  }
  if (status === 403) {
    if (code === 'transfer_forbidden') return new TransferError('not_allowed', status, code)
    if (code === 'transfer_not_allowed_by_rule') return new TransferError('rule_denied', status, code)
    if (code === 'invalid_token') return new TransferError('link_invalid', status, code)
    return new TransferError('unknown', status, code)
  }
  if (status === 410 && code === 'link_expired') return new TransferError('link_expired', status, code)
  if (status === 409) {
    if (code === 'source_ticket_not_active') return new TransferError('not_active', status, code)
    if (code === 'transfer_hold_already_pending') return new TransferError('hold_pending', status, code)
    // cross_currency_transfer_unsupported, and the v3 message-only (no code)
    // hold 409s — "insufficient capacity for ticket type …" and "org_unit is
    // not enabled to take charges" — are not the buyer's to fix: Contact support.
    return new TransferError('unknown', status, code)
  }
  if (status === 503) {
    // signing_not_configured (#273) and, v3 (#276), transfer_line_kinds_
    // unavailable (migration 0014 absent) — both "not available yet".
    if (code === 'signing_not_configured' || code === 'transfer_line_kinds_unavailable') {
      return new TransferError('unavailable', status, code)
    }
    return new TransferError('unknown', status, code)
  }
  // Every 400 is 'invalid' — incl. the v3 codes dest_ticket_type_id_invalid
  // (non-UUID, #275) and person_transfer_type_mismatch (#276).
  if (status === 400) return new TransferError('invalid', status, code)
  return new TransferError('unknown', status, code)
}

function isHoldResponse(v: unknown): v is TransferHoldResponse {
  const r = v as TransferHoldResponse | null
  return (
    !!r &&
    typeof r.transfer?.hold_expires_at === 'string' &&
    typeof r.order?.order_id === 'string' &&
    typeof r.preview?.total_minor === 'number' &&
    typeof r.preview?.currency === 'string'
  )
}

/**
 * POST ${base}/transfers — hold a person transfer (15-min pending order) and
 * get the priced preview. Closing the drawer lets the hold lapse passively
 * (there is no cancel call — BACKEND).
 */
export async function createTransferHold(
  input: TransferHoldInput,
  auth: TransferAuth,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<TransferHoldResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (auth.kind === 'bearer') headers.Authorization = `Bearer ${auth.idToken}`
  else headers[TRANSFER_LINK_HEADER] = auth.token

  const body = {
    source_ticket_id: input.sourceTicketId,
    dest_ticket_type_id: input.destTicketTypeId,
    transfer_type: 'person',
    recipient_email: input.recipientEmail,
  }

  let res: Response
  try {
    res = await fetchImpl(`${base.replace(/\/$/, '')}/transfers`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
  } catch {
    throw new TransferError('unknown')
  }
  if (!res.ok) throw transferFailureFor(res.status, await readErrorBody(res), auth.kind)
  let json: unknown
  try {
    json = await res.json()
  } catch {
    throw new TransferError('unknown', res.status)
  }
  if (!isHoldResponse(json)) throw new TransferError('unknown', res.status, 'unexpected_shape')
  return json
}

/**
 * Free vs paid — TICKETS-CONTRACT-TRANSFER-HOLD-V1 v3 <checkout_handoff>:
 * "When order.total_minor = 0: POST /box-office/orders/<order_id>/complete-
 * free". The HOLD's order.total_minor decides it, NEVER preview.zero_total
 * (a preview flag is not the order the server will complete; since #276 the
 * order total includes the transfer fee, so /total == preview.total_minor).
 */
export function isFreeHold(hold: TransferHoldResponse): boolean {
  return typeof hold.order?.total_minor === 'number' && hold.order.total_minor === 0
}

/** complete-free success outcomes a transfer order can return (tickets
 *  finalizePaidOrder, payment-consumer.mjs). */
export type CompleteFreeOutcome = 'paid_and_switched' | 'paid_transfer_not_switched' | 'already_paid_noop'

export interface CompleteFreeResult {
  outcome: CompleteFreeOutcome
  order_id: string
  [k: string]: unknown
}

const COMPLETE_FREE_OUTCOMES: ReadonlySet<string> = new Set<CompleteFreeOutcome>([
  'paid_and_switched', 'paid_transfer_not_switched', 'already_paid_noop',
])

/** Delays before the 2nd and 3rd attempt (ms). */
export const COMPLETE_FREE_BACKOFF_MS = [300, 900] as const

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * POST ${base}/orders/<order_id>/complete-free — finalise a £0 transfer hold
 * (contract <checkout_handoff>). Only call when isFreeHold(hold).
 *
 * CALLER PROOF (tickets #289, BOARDROOM-2026-09-28-013738): complete-free on
 * an order_kind='transfer' order now requires the SAME proof as the hold —
 * the consumer Bearer whose verified email owns the SOURCE order (or an
 * operator / the transfer link); unauthenticated ⇒ 401 missing_proof. So the
 * caller passes the SAME TransferAuth it used for createTransferHold, sent the
 * same way (Bearer, or X-Transfer-Link for the guest-link path).
 *
 * A 401/403 here is NOT retried. It maps through transferFailureFor exactly
 * like the hold: the preview can stay open until the hold lapses and the ID
 * token can expire inside that window, so a Bearer-path 401 is 'auth' (the
 * sign-in state), 403 transfer_forbidden is 'not_allowed', and link-path codes
 * stay link errors. ONE log line carries only the status and the server's
 * machine code (never the token, never an email). The drawer also refreshes
 * the session (ensureLiveSession) right before calling, so an expired token
 * is usually healed silently first.
 *
 * IDEMPOTENT RETRY: the server's paid-flip is exactly-once ("Callers may
 * retry safely"), so a network error or a 5xx is retried up to 2 more times
 * with a short backoff; a repeat that finds the order already paid answers
 * already_paid_noop, which is success. A 4xx is NOT retried: 409 "order is
 * not free" (and any other 4xx — message-only bodies, no code) is 'unknown'
 * → Contact support.
 *
 * CONTRACT GAP: already_paid_noop is ALSO what tickets returns for an order
 * that never reached a payable state (e.g. a lapsed hold), so the UI cannot
 * tell "already completed" from "nothing happened"; it shows the honest
 * pending copy, never "Transfer sent".
 */
export async function completeFreeTransfer(
  orderId: string,
  auth: TransferAuth,
  base: string,
  fetchImpl: typeof fetch = fetch,
  sleep: (ms: number) => Promise<void> = realSleep,
): Promise<CompleteFreeResult> {
  const url = `${base.replace(/\/$/, '')}/orders/${encodeURIComponent(orderId)}/complete-free`
  const headers: Record<string, string> = {}
  if (auth.kind === 'bearer') headers.Authorization = `Bearer ${auth.idToken}`
  else headers[TRANSFER_LINK_HEADER] = auth.token
  const attempts = COMPLETE_FREE_BACKOFF_MS.length + 1
  let lastStatus: number | null = null
  for (let i = 0; i < attempts; i++) {
    if (i > 0) await sleep(COMPLETE_FREE_BACKOFF_MS[i - 1])
    let res: Response
    try {
      res = await fetchImpl(url, { method: 'POST', headers })
    } catch {
      lastStatus = null
      continue // network error — retry
    }
    if (res.status >= 500) {
      lastStatus = res.status
      continue // 5xx — retry (safe: the server flip is idempotent)
    }
    if (!res.ok) {
      const body = await readErrorBody(res)
      if (res.status === 401 || res.status === 403) {
        // Status + machine code only — never the token, never an email.
        console.warn(`[transfer] complete-free refused status=${res.status} code=${body.code ?? 'none'}`)
        // Same typed mapping as the hold (boardroom #231 review MED): a 401 on
        // the Bearer path is 'auth' (sign-in), 403 transfer_forbidden is
        // 'not_allowed', link-path codes stay link errors. Still NOT retried.
        throw transferFailureFor(res.status, body, auth.kind)
      }
      throw new TransferError('unknown', res.status, body.code)
    }
    let json: unknown
    try {
      json = await res.json()
    } catch {
      throw new TransferError('unknown', res.status, 'unexpected_shape')
    }
    const r = json as CompleteFreeResult | null
    if (!r || typeof r.outcome !== 'string' || !COMPLETE_FREE_OUTCOMES.has(r.outcome)) {
      throw new TransferError('unknown', res.status, 'unexpected_shape')
    }
    return r
  }
  throw new TransferError('unknown', lastStatus)
}

/*
 * CONTRACT GAP (paid transfers, order.total_minor > 0): the contract says to
 * pay the hold's order via {payments}/payments/create-intent with
 * {orderRef, amount, currency, connectedAccountId, feePct, idempotencyKey}.
 * my.trumin has NO source for connectedAccountId / feePct / paymentModel, no
 * Stripe publishable key, no return_url / 3-D Secure handling and no
 * post-payment status endpoint to poll — so the paid Confirm stays DISABLED
 * ("Card payment for transfers is coming soon") until those are defined.
 */
